import React, { createContext, useContext, useState } from 'react';
import { authApi } from '../services/api';

interface User {
  id: string;
  email: string;
  name: string;
  picture: string;
  phone: string | null;
  role: string;
}

interface AuthContextType {
  user: User | null;
  login: (credential: string) => Promise<void>;
  loginWithPassword: (email: string, password: string) => Promise<void>;
  logout: () => void;
  isAuthenticated: boolean;
  isLoading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Read the stored session SYNCHRONOUSLY so the very first render already
  // knows the user is signed in. Doing this in a useEffect made the first
  // render unauthenticated, so ProtectedRoute bounced through /login and
  // dropped any query string (breaking /leads?tag=… deep links).
  const [user, setUser] = useState<User | null>(() => {
    try {
      const storedUser = localStorage.getItem('touchpoint_portal_user');
      if (storedUser) {
        const parsed = JSON.parse(storedUser);
        if (parsed.id) return parsed;
        // Stale format from before backend verification — force re-login
        localStorage.removeItem('touchpoint_portal_user');
      }
    } catch {
      localStorage.removeItem('touchpoint_portal_user');
    }
    return null;
  });
  const [isLoading, setIsLoading] = useState(false);

  const login = async (credential: string) => {
    setIsLoading(true);
    try {
      // Decode Google JWT to extract user info
      const base64Url = credential.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64).split('').map((c) =>
          '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)
        ).join('')
      );
      const googleData = JSON.parse(jsonPayload);

      // Verify with backend
      const verifiedUser = await authApi.verifyUser(
        googleData.email,
        googleData.name,
        googleData.picture,
        googleData.sub
      );

      const userInfo: User = {
        id: verifiedUser.id,
        email: verifiedUser.email,
        name: verifiedUser.name || googleData.name,
        picture: googleData.picture,
        phone: verifiedUser.phone,
        role: verifiedUser.role,
      };

      setUser(userInfo);
      localStorage.setItem('touchpoint_portal_user', JSON.stringify(userInfo));
    } catch (error: any) {
      if (error?.response?.status === 403) {
        alert('Access denied. Your email is not authorized. Contact admin to get access.');
      } else {
        alert('Login failed. Please try again.');
        console.error('Login error:', error);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const loginWithPassword = async (email: string, password: string) => {
    setIsLoading(true);
    try {
      const verifiedUser = await authApi.loginWithPassword(email, password);
      const userInfo: User = {
        id: verifiedUser.id,
        email: verifiedUser.email,
        name: verifiedUser.name || '',
        picture: verifiedUser.google_picture_url || '',
        phone: verifiedUser.phone,
        role: verifiedUser.role,
      };
      setUser(userInfo);
      localStorage.setItem('touchpoint_portal_user', JSON.stringify(userInfo));
    } catch (error: any) {
      if (error?.response?.status === 403) {
        alert('Access denied. Email or password is incorrect.');
      } else {
        alert('Login failed. Please try again.');
        console.error('Password login error:', error);
      }
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = () => {
    setUser(null);
    localStorage.removeItem('touchpoint_portal_user');
  };

  return (
    <AuthContext.Provider
      value={{ user, login, loginWithPassword, logout, isAuthenticated: !!user, isLoading }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
